/**
 * deposits request CONTRACTS — the canonical, strict Zod schema for the BODY of
 * each deposit write endpoint.
 *
 * These bodies MOVE REAL MONEY: `POST /deposits` opens a Stripe Connect
 * checkout on the clinic's connected account, and `POST /deposits/:id/refund`
 * sends money back. No body carries an amount or a currency (ENG-1306): the
 * server derives both from the clinic's deposit policy and connected account.
 * Every constraint below is lifted VERBATIM from the feature schema it now
 * feeds; nothing here was widened to make a caller parse. If a constraint looks
 * surprising, it is deliberate — tighten it in this file (both halves move
 * together) rather than working around it at a call site.
 *
 * DIRECTION OF DERIVATION — wire -> server
 * ---------------------------------------
 * This file is the SOURCE. The feature schemas DERIVE from it by `.extend()`ing
 * the server-injected context fields onto the base:
 *
 *   packages/features/src/appointments/services/create-deposit-link/
 *     createDepositLinkSchema = createDepositLinkRequestBase.extend({ organizationId, actor })
 *   packages/features/src/appointments/services/send-deposit-link/
 *     sendDepositLinkSchema = sendDepositLinkRequestBase.extend({ depositId, organizationId, actor })
 *   packages/features/src/appointments/services/refund-deposit/
 *     refundDepositSchema = refundDepositRequestBase.extend({ depositId, organizationId })
 *
 * Because each server schema literally IS the wire schema plus fields, it can
 * never be laxer than the wire schema, and no drift is possible. Do NOT invert
 * this — a contract hand-copied from the feature schema is exactly the drift
 * this package exists to eliminate.
 *
 * BASE vs SCHEMA — why two exports
 * --------------------------------
 * `.refine()` returns a `ZodEffects`, which has NO `.extend()`. So every
 * contract file exports a matched pair:
 *
 *  - `<name>RequestBase`   — a plain `z.object({…})`. EXTENDABLE. This is what
 *    the backend feature schema extends with its context fields. It is NOT
 *    strict, because `.strict()` would reject the very fields being added.
 *  - `<name>RequestSchema` — `<name>RequestBase.strict()`. This is what
 *    VALIDATES a wire body: unknown fields are REJECTED, so a client sending a
 *    stale, renamed, or typo'd key fails loudly instead of having it silently
 *    stripped by a permissive `z.object`.
 *
 * Context fields the SERVER injects are absent from both body contracts:
 *  - `organizationId` — from the active-org session, never sent by the client.
 *  - `depositId`      — the `:id` route param on the refund endpoint.
 */
import { z } from 'zod';
import { externalRedirectUrl } from './redirect-url.js';
/**
 * `POST /deposits` body — the EXTENDABLE half. Creates a deposit payment LINK
 * for a proposed booking.
 *
 * MONEY, by omission. There is deliberately NO `amountCents` and NO `currency`:
 * the amount is computed server-side from the clinic/service deposit policy
 * (`resolveBookingPayment`) and the currency is the clinic's connected
 * account's. A client — or a model — that sends either gets a parse error from
 * the strict schema below, never a charge for the figure it chose. There is no
 * staff override in v1.
 *
 * No `expirationHours` either: the hold (and the link) last
 * `organization.holdExpirationHours`, clamped to what Stripe Checkout allows.
 *
 * `successUrl` / `cancelUrl` are OPTIONAL redirect targets (default: the
 * clinic's own booking page). When sent they are `externalRedirectUrl`-checked,
 * because they are handed to Stripe verbatim.
 *
 * `conversationId` optionally pins the conversation the link will be sent in.
 */
export const createDepositLinkRequestBase = z.object({
    appointmentId: z.string().min(1),
    conversationId: z.string().min(1).optional(),
    successUrl: externalRedirectUrl.optional(),
    cancelUrl: externalRedirectUrl.optional(),
});
/**
 * `POST /deposits` body — the VALIDATING half. Unknown keys are rejected, so
 * `amountCents`, `currency` or `organizationId` in a body is an error.
 */
export const createDepositRequestSchema = createDepositLinkRequestBase.strict();
/**
 * `POST /deposits/:id/send` body — the EXTENDABLE half. Sends (or re-sends)
 * the live link to the patient through the existing conversation channel.
 * The deposit is the `:id` route param; the org and the sender are the
 * session's. `conversationId` overrides which conversation is used.
 */
export const sendDepositLinkRequestBase = z.object({
    conversationId: z.string().min(1).optional(),
});
/** `POST /deposits/:id/send` body — the VALIDATING half. */
export const sendDepositLinkRequestSchema = sendDepositLinkRequestBase.strict();
/**
 * `POST /deposits/:id/refund` body — the EXTENDABLE half.
 *
 * MONEY, and almost empty on purpose. The deposit being refunded is identified
 * by the `:id` ROUTE PARAM, and the org by the session; neither may be supplied
 * in the body, which is why `.strict()` matters more here than anywhere else in
 * this file. A client that tried to pass `depositId` in the body — refunding a
 * deposit other than the one in the URL — now gets a parse error rather than a
 * silently ignored key.
 *
 * `reason` is the Stripe refund-reason vocabulary and is a closed `z.enum`, not
 * a free string: Stripe rejects unknown reasons, and the set is Stripe's rather
 * than ours, so it is spelled out literally here instead of being pulled from
 * `@borradh-workspace/labels`. It is optional — Stripe treats an absent reason
 * as an unspecified refund.
 *
 * There is no `amountCents`: refunds here are always FULL refunds of the
 * recorded deposit. Adding a partial-refund amount is a server change, not a
 * contract one — do not add the field here first.
 */
export const refundDepositRequestBase = z.object({
    reason: z
        .enum(['requested_by_customer', 'duplicate', 'fraudulent'])
        .optional(),
});
/**
 * `POST /deposits/:id/refund` body — the VALIDATING half.
 */
export const refundDepositRequestSchema = refundDepositRequestBase.strict();
//# sourceMappingURL=deposits.js.map